const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/utils/colorUtils.ts';
/**
 * colorUtils.ts
 *
 * Utility function related to colour values.
 */

import Imm from 'immutable';
import * as MathUtils from './mathUtils';
import { colors, kisimColors } from '../defs/colors';

const emptyMap = Imm.Map<string, any>();
const emptyList = Imm.List<any>();

export interface IColor {
    r: number;
    g: number;
    b: number;
}

/**
 * @param color Must be either #rgb or #rrggbb. Alpha is supported, too (#rgba, #rrggbbaa), but is discarded.
 * @return A color structure
 */
export function parseColor(color: string): IColor {
    if (color === 'transparent') {
        return { r: 255, g: 255, b: 255 };
    }
    if (
        color.charAt(0) !== '#' ||
        !(color.length === 4 || color.length === 7 || color.length === 5 || color.length === 9)
    ) {
        throw new Error('Bad colour format');
    }

    let sr: string;
    let sg: string;
    let sb: string;

    if (color.length === 4 || color.length === 5) {
        sr = `${color.charAt(1)}${color.charAt(1)}`;
        sg = `${color.charAt(2)}${color.charAt(2)}`;
        sb = `${color.charAt(3)}${color.charAt(3)}`;
    } else {
        sr = color.slice(1, 3);
        sg = color.slice(3, 5);
        sb = color.slice(5, 7);
    }

    return {
        r: Number.parseInt(sr, 16),
        g: Number.parseInt(sg, 16),
        b: Number.parseInt(sb, 16),
    };
}

/**
 * Converts Hue/Saturation/Lightness to RGB.
 * (Not to be confused with Hue/Saturation/Value!)
 * @param hue 0..1
 * @param sat 0..1
 * @param lgt 0..1
 * @return An RGB colour whose components are 0..1
 */
export function hslToNormalizedRGB(hue: number, sat: number, lgt: number): IColor {
    if (sat <= 0) {
        return { r: lgt, g: lgt, b: lgt };
    }

    const q = lgt < 0.5 ? lgt * (1 + sat) : lgt + sat - lgt * sat;
    const p = 2 * lgt - q;

    // red

    let r = hue + 1 / 3;
    r = r < 0 ? r + 1 : r > 1 ? r - 1 : r;

    if (r < 1 / 6) {
        r = p + (q - p) * 6 * r;
    } else if (r < 0.5) {
        r = q;
    } else if (r < 2 / 3) {
        r = p + (q - p) * (2 / 3 - r) * 6;
    } else {
        r = p;
    }

    // green

    let g = hue;
    g = g < 0 ? g + 1 : g > 1 ? g - 1 : g;

    if (g < 1 / 6) {
        g = p + (q - p) * 6 * g;
    } else if (g < 0.5) {
        g = q;
    } else if (g < 2 / 3) {
        g = p + (q - p) * (2 / 3 - g) * 6;
    } else {
        g = p;
    }

    // blue

    let b = hue - 1 / 3;
    b = b < 0 ? b + 1 : b > 1 ? b - 1 : b;

    if (b < 1 / 6) {
        b = p + (q - p) * 6 * b;
    } else if (b < 0.5) {
        b = q;
    } else if (b < 2 / 3) {
        b = p + (q - p) * (2 / 3 - b) * 6;
    } else {
        b = p;
    }

    return { r, g, b };
}

/**
 * Converts a normalized RGB to an RGB colour whose components are 0..255
 * @param rgb components are expected to be 0..1.
 * @return A new object
 */
export function denormalizeRGB(rgb: IColor): IColor {
    return {
        r: rgb.r * 255,
        g: rgb.g * 255,
        b: rgb.b * 255,
    };
}

/**
 * This method may be used to determine if text over a coloured background
 * should be bright or dark. A colour is considered to be dark if the perceived
 * contrast towards white is higher than that towards black.
 * @param color Must be either #rgb or #rrggbb.
 * @return: true if the given colour is considered to be a dark hue; false
 *    if it is considered to be a light hue.
 */
export function isDarkColor(color: string): boolean {
    const c = parseColor(color);
    return isDarkRGB(c.r, c.g, c.b);
}

/**
 * @see isDarkColor
 * Calculated according to WCAG 2.0
 * @param r red component, 0..255
 * @param g green component, 0..255
 * @param b blue component, 0..255
 * @return: true if the given colour is considered to be a dark hue; false
 *    if it is considered to be a light hue.
 */
export function isDarkRGB(r: number, g: number, b: number): boolean {
    const ur = r / 255;
    const ug = g / 255;
    const ub = b / 255;

    const relativeR = ur <= 0.003928 ? ur / 12.92 : Math.pow((ur + 0.055) / 1.055, 2.4);
    const relativeG = ug <= 0.003928 ? ug / 12.92 : Math.pow((ug + 0.055) / 1.055, 2.4);
    const relativeB = ub <= 0.003928 ? ub / 12.92 : Math.pow((ub + 0.055) / 1.055, 2.4);

    const wr = 0.05;
    const wg = 0.7152;
    const wb = 0.05;
    const luma = (wr * relativeR + wg * relativeG + wb * relativeB) / (wr + wg + wb);

    const contrastRatioToBlack = (luma + 0.05) / 0.05;
    const contrastRatioToWhite = (1 + 0.05) / (luma + 0.05);

    return 0.7 * contrastRatioToBlack < contrastRatioToWhite;
}

/**
 * Constructs a CSS colour string from the given colour component values.
 * @param r red 0..255
 * @param g green 0..255
 * @param b blue 0..255
 * @return: a string
 */
export function makeRGB(r: number, g: number, b: number) {
    r = MathUtils.clamp(Math.round(r), 0, 255);
    g = MathUtils.clamp(Math.round(g), 0, 255);
    b = MathUtils.clamp(Math.round(b), 0, 255);
    const sr = `${r < 16 ? '0' : ''}${r.toString(16)}`;
    const sg = `${g < 16 ? '0' : ''}${g.toString(16)}`;
    const sb = `${b < 16 ? '0' : ''}${b.toString(16)}`;
    return `#${sr}${sg}${sb}`;
}

/**
 * Constructs a HTML colour string from a given map containing the components R, G, B.
 * Alternatively, the components may be named Red, Green, Blue.
 * Colour component values must be in the range 0..255.
 * @return: a string
 */
export function makeRGBFromMap(map: Imm.Map<string, any>) {
    let r: any;
    let g: any;
    let b: any;

    if (map) {
        if (map.has('Red')) {
            r = map.get('Red');
            g = map.get('Green');
            b = map.get('Blue');
        } else if (map.has('r')) {
            r = map.get('r');
            g = map.get('g');
            b = map.get('b');
        } else {
            r = map.get('R');
            g = map.get('G');
            b = map.get('B');
        }
    }

    return makeRGB(Number(r || 0), Number(g || 0), Number(b || 0));
}

export function convertMapToColor(map: Imm.Map<string, any> | null | undefined): IColor | undefined {
    if (!map) return undefined;
    return {
        r: Number(map.get('R') || map.get('r') || 0),
        g: Number(map.get('G') || map.get('g') || 0),
        b: Number(map.get('B') || map.get('b') || 0),
    };
}

/**
 * This function converts KisimColor into our color palette
 * @param color KisimColor string (e.g. Traube500, Rot400,...)
 * @return: the converted color string
 */
export const convertKisimColorToRGB = (color: string) => {
    if (kisimColors[color]) {
        return kisimColors[color];
    }

    // Separate string into text and value z.B Traube500 => ["Traube", "500"]
    const colorObject = color.split(/([0-9]+)/).filter(Boolean);
    const colorName = colorObject[0];
    const colorValue = colorObject[1] || '500';

    const map = {
        Rot: (colors as any).red,
        Pink: (colors as any).pink,
        Purple: (colors as any).deepPurple,
        Traube: (colors as any).purple,
        Blau: (colors as any).blue,
        Cyan: (colors as any).cyan,
        Gruen: (colors as any).green,
        Limone: (colors as any).lime,
        Gelb: (colors as any).yellow,
        Orange: (colors as any).orange,
        Braun: (colors as any).brown,
        LayoutGrau: (colors as any).grey,
        Grau: (colors as any).grey,
        Violet: (colors as any).purple,
        Yellow: (colors as any).yellow,
        Red: (colors as any).red,
        Grey: (colors as any).grey,
    };
    return (map as any)[colorName]?.[colorValue];
};

/**
 * Gets the colorDef from the content using the key
 * @param content the color definitions
 * @param key this is the name of the color we want to extract
 * @return colors the cleaned up colorDef
 */
export const getColorDef = (content: Imm.Map<string, any>, key: string) => {
    let colors = emptyMap;
    (content?.get(key) || emptyList).forEach((item: Imm.Map<string, any>) => {
        const colorDef = item.get('CONTENT');
        if (colorDef) {
            const val = colorDef.get('Val') || '?';
            colors = colors.set(val, colorDef.delete('Val'));
        }
    });
    return colors;
};
